import {
  DatePicker,
  ErrorMessage,
  HStack,
  Pressable,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import React from 'react';
import { useToken } from '@chakra-ui/react';
import { ArrowLeft } from 'lucide-react';
import { useDatePickerWithSelectedMeetingType } from './hooks/useDatePickerWithSelectedMeetingType';
import { ProviderType } from '../../api/types';
import { computeTimezoneAbbreviation } from './utils';
import { MeetingList } from './MeetingList';
import { MeetingType } from '../../models/meeting';
import { IconButton } from '../Form/IconButton';
import { AppointmentProviderType } from '../../models/appointment-type';

type Props = {
  careMemberType: AppointmentProviderType;
  isError: boolean;
  isSubmitting: boolean;
  meetingType: MeetingType | null;
  onGoBack: () => void;
  onSubmit: (
    selectedDateTime: string,
    hostId: string,
    meetingTypeId: string
  ) => void;
  providerType: ProviderType | null;
};

export const MeetingDatePicker = ({
  careMemberType,
  isError,
  isSubmitting,
  meetingType,
  onGoBack,
  onSubmit,
  providerType,
}: Props) => {
  const {
    availabilityMap,
    dayMeetings,
    isError: isAvailabilityFetchError,
    isFetching,
    meetingTypeId,
    onMonthChange,
    onSelectDate,
    selectedDate,
    selectedMonth,
    timezone,
  } = useDatePickerWithSelectedMeetingType({
    careMemberType,
    meetingType,
    providerType,
  });
  const [blueGray600] = useToken('colors', ['blueGray.600']);

  return (
    <VStack>
      <HStack alignItems={'center'} mb={4} gap={1}>
        <Pressable>
          <IconButton onClick={onGoBack}>
            <ArrowLeft color={blueGray600} size={16} />
          </IconButton>
        </Pressable>

        <Text color={'blueGray.600'} fontSize={'14px'} fontWeight={'normal'}>
          Step 2: Select Date &amp; Time of Visit
        </Text>
      </HStack>

      {isError && (
        <VStack mb={4}>
          <ErrorMessage
            message={
              'An error occurred while creating the appointment. Please try again later.'
            }
          />
        </VStack>
      )}

      <HStack gap={'32px'}>
        <DatePicker
          availableDates={availabilityMap}
          isFetching={isFetching}
          onMonthChange={onMonthChange}
          onSelectDate={onSelectDate}
          selectedDate={selectedDate}
          selectedMonth={selectedMonth.startDate}
          timezone={timezone}
        />

        <VStack alignItems={'center'} flex={1} justifyContent={'start'}>
          <HStack justifyContent={'end'} my={4} w={'100%'}>
            <Text
              color={'blueGray.600'}
              fontSize={'14px'}
              fontWeight={'normal'}
            >
              Member Timezone: <b>{computeTimezoneAbbreviation(timezone)}</b>
            </Text>
          </HStack>

          <MeetingList
            isError={isAvailabilityFetchError}
            isFetching={isFetching}
            isSubmitting={isSubmitting}
            meetings={dayMeetings}
            onSubmit={(selectedTime: string, selectedHostId: string) => {
              onSubmit(selectedTime, selectedHostId, meetingTypeId);
            }}
            timezone={timezone}
          />
        </VStack>
      </HStack>
    </VStack>
  );
};
